import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { AutoComplete, Input, Spin } from 'antd';
import type { InputProps } from 'antd';
import type { ParsedGooglePlace } from '../utils/addressUtils';
import { parseGooglePlace } from '../utils/addressUtils';

const libraries: 'places'[] = ['places'];

export interface AddressAutocompleteInputProps extends Omit<InputProps, 'onChange'> {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Fired when user picks a Places suggestion (in addition to updating the address line). */
  onPlaceResolved?: (place: ParsedGooglePlace) => void;
}

type PredictionOption = {
  value: string;
  label: React.ReactNode;
};

const InnerAutocomplete: React.FC<AddressAutocompleteInputProps> = ({
  onPlaceResolved,
  onChange,
  value,
  ...inputProps
}) => {
  const acServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const descriptionToPlaceIdRef = useRef<Map<string, string>>(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [options, setOptions] = useState<PredictionOption[]>([]);
  const [fetchingDetails, setFetchingDetails] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !(window as any).google?.maps?.places) return;
    acServiceRef.current = new (window as any).google.maps.places.AutocompleteService();
    placesServiceRef.current = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
  }, []);

  const fetchPredictions = useCallback((text: string) => {
    const svc = acServiceRef.current;
    const map = descriptionToPlaceIdRef.current;
    map.clear();
    if (!svc || !text.trim() || text.trim().length < 2) {
      setOptions([]);
      return;
    }
    svc.getPlacePredictions(
      {
        input: text.trim(),
        types: ['address'],
      },
      (predictions: any[] | null, status: string) => {
        if (status !== 'OK' || !predictions?.length) {
          setOptions([]);
          return;
        }
        const next: PredictionOption[] = [];
        for (const p of predictions) {
          map.set(p.description, p.place_id);
          const main = p.structured_formatting?.main_text ?? p.description;
          const secondary = p.structured_formatting?.secondary_text ?? '';
          next.push({
            value: p.description,
            label: (
              <div style={{ lineHeight: 1.35, padding: '2px 0' }}>
                <div style={{ fontWeight: 500 }}>{main}</div>
                {secondary ? (
                  <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginTop: 2 }}>{secondary}</div>
                ) : null}
              </div>
            ),
          });
        }
        setOptions(next);
      }
    );
  }, []);

  const schedulePredictions = useCallback(
    (text: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchPredictions(text), 280);
    },
    [fetchPredictions]
  );

  const handleSelect = useCallback(
    (selectedDescription: string) => {
      const placeId = descriptionToPlaceIdRef.current.get(selectedDescription);
      const svc = placesServiceRef.current;
      if (!placeId || !svc) return;

      setFetchingDetails(true);
      setOptions([]);
      svc.getDetails(
        {
          placeId,
          fields: ['address_components', 'formatted_address', 'geometry', 'name'],
        },
        (place: any, status: string) => {
          setFetchingDetails(false);
          if (status !== 'OK' || !place?.address_components?.length) return;
          const parsed = parseGooglePlace(place);
          onPlaceResolved?.(parsed);
          const synthetic = {
            target: { value: parsed.streetLine },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange?.(synthetic);
        }
      );
    },
    [onChange, onPlaceResolved]
  );

  return (
    <AutoComplete
      style={{ width: '100%' }}
      value={value}
      options={options}
      onSelect={handleSelect}
      onChange={(text) => {
        onChange?.({ target: { value: text } } as React.ChangeEvent<HTMLInputElement>);
        schedulePredictions(String(text));
      }}
      notFoundContent={null}
    >
      <Input
        {...inputProps}
        suffix={fetchingDetails ? <Spin size="small" /> : inputProps.suffix}
      />
    </AutoComplete>
  );
};

/**
 * Street address with Google Places suggestions when `REACT_APP_GOOGLE_MAPS_API_KEY` is set.
 * Uses AutocompleteService so each row shows a primary line plus Google's secondary line
 * (often includes city, state, and postal code). Place Details runs on selection to fill fields.
 * Otherwise a plain Ant Design Input (manual entry unchanged).
 */
const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return <Input {...props} />;
  }
  return <AddressAutocompleteWithScript {...props} />;
};

const AddressAutocompleteWithScript: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  if (loadError) {
    return <Input {...props} placeholder={props.placeholder || 'Enter street address'} />;
  }
  if (!isLoaded) {
    return <Input {...props} disabled placeholder="Loading address search…" />;
  }
  return <InnerAutocomplete {...props} />;
};

export default AddressAutocompleteInput;
